import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHistory, FaFilter, FaArrowLeft, FaPlus } from 'react-icons/fa';
import api from '../../../src/api/axios';
import RequestCard from '../../components/RequestCard/RequestCard';
import './RoleRequestHistory.css';

const RoleRequestHistory = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    applyFilter();
  }, [filter, requests]);

  const fetchRequests = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/api/my-role-requests');
      setRequests(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError('Failed to load request history.');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilter = () => {
    if (filter === 'all') {
      setFilteredRequests(requests);
    } else {
      setFilteredRequests(requests.filter(r => r.status === filter));
    }
  };

  const getStatusCounts = () => {
    return {
      all: requests.length,
      pending: requests.filter(r => r.status === 'pending').length,
      approved: requests.filter(r => r.status === 'approved').length,
      rejected: requests.filter(r => r.status === 'rejected').length,
    };
  };

  const counts = getStatusCounts();

  if (isLoading) {
    return (
      <div className="rrh-container">
        <div className="rrh-loading">Loading request history...</div>
      </div>
    );
  }

  return (
    <div className="rrh-container">
      <div className="rrh-header">
        <div className="rrh-header-content">
          <div className="rrh-title-section">
            <FaHistory className="rrh-icon" />
            <div>
              <h1 className="rrh-title">Role Request History</h1>
              <p className="rrh-subtitle">Track the status of your role assignment requests</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/role_selection')}
            className="rrh-new-request-btn"
          >
            <FaPlus /> New Request
          </button>
        </div>
      </div>

      {error && (
        <div className="rrh-error-banner">
          {error}
        </div>
      )}

      <div className="rrh-filters">
        <button
          className={`rrh-filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({counts.all})
        </button>
        <button
          className={`rrh-filter-btn ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          Pending ({counts.pending})
        </button>
        <button
          className={`rrh-filter-btn ${filter === 'approved' ? 'active' : ''}`}
          onClick={() => setFilter('approved')}
        >
          Approved ({counts.approved})
        </button>
        <button
          className={`rrh-filter-btn ${filter === 'rejected' ? 'active' : ''}`}
          onClick={() => setFilter('rejected')}
        >
          Rejected ({counts.rejected})
        </button>
      </div>

      <div className="rrh-content">
        {filteredRequests.length === 0 ? (
          <div className="rrh-empty-state">
            <FaHistory className="rrh-empty-icon" />
            <h2>No Requests Found</h2>
            <p>
              {filter === 'all'
                ? "You haven't submitted any role requests yet."
                : `You don't have any ${filter} requests.`}
            </p>
            <button
              onClick={() => navigate('/role_selection')}
              className="rrh-empty-action-btn"
            >
              <FaPlus /> Create Your First Request
            </button>
          </div>
        ) : (
          <div className="rrh-requests-list">
            {filteredRequests.map((request) => (
              <RequestCard
                key={request.id}
                request={request}
                showDetails={true}
              />
            ))}
          </div>
        )}
      </div>

      <div className="rrh-footer">
        <button
          onClick={() => navigate(-1)}
          className="rrh-back-btn"
        >
          <FaArrowLeft /> Go Back
        </button>
      </div>
    </div>
  );
};

export default RoleRequestHistory;
